import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  CheckCircle2,
  XCircle,
  ShieldCheck,
  FileText,
  Loader2,
  AlertCircle,
  User,
  Filter,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

type UploadRow = {
  id: string;
  createdAt: string;
  userId: string | null;
  fileName: string | null;
  passed: boolean;
  blocked: boolean;
  verdict: string | null;
};

type SummaryResponse = {
  stats: { scanned: number; passed: number; blocked: number };
  recentUploads: UploadRow[];
};

function StatTile({
  label,
  value,
  icon,
  variant,
}: {
  label: string;
  value: number;
  icon: React.ReactNode;
  variant: "neutral" | "green" | "red";
}) {
  const colorClass =
    variant === "green"
      ? "text-emerald-600 dark:text-emerald-400"
      : variant === "red"
        ? "text-red-600 dark:text-red-400"
        : "text-foreground";
  return (
    <Card data-testid={`stat-${label.toLowerCase().replace(/\W+/g, "-")}`}>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">{label}</p>
            <p className={`text-2xl font-semibold tabular-nums ${colorClass}`}>
              {value.toLocaleString()}
            </p>
          </div>
          <div className={`mt-1 ${colorClass} opacity-70`}>{icon}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function ResultBadge({ row }: { row: UploadRow }) {
  if (row.blocked) {
    return (
      <span
        className="inline-flex items-center gap-1 text-red-700 dark:text-red-400 text-sm font-medium"
        data-testid="result-blocked"
      >
        <XCircle className="w-4 h-4 shrink-0" />
        Blocked
      </span>
    );
  }
  if (row.passed) {
    return (
      <span
        className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400 text-sm font-medium"
        data-testid="result-clean"
      >
        <CheckCircle2 className="w-4 h-4 shrink-0" />
        Clean
      </span>
    );
  }
  return (
    <Badge variant="secondary" data-testid="result-pending">
      Pending
    </Badge>
  );
}

export function DocumentSecurityPanel() {
  const [userId, setUserId] = useState("");
  const [sinceDate, setSinceDate] = useState("");
  const [untilDate, setUntilDate] = useState("");

  const queryParams = useMemo(() => {
    const p = new URLSearchParams();
    if (userId.trim()) p.set("userId", userId.trim());
    if (sinceDate) p.set("since", new Date(`${sinceDate}T00:00:00`).toISOString());
    if (untilDate) p.set("until", new Date(`${untilDate}T23:59:59.999`).toISOString());
    return p.toString();
  }, [userId, sinceDate, untilDate]);

  const { data, isLoading, error, refetch, isFetching } = useQuery<SummaryResponse>({
    queryKey: ["/api/admin/document-security/summary", queryParams],
    queryFn: async () => {
      const res = await fetch(`/api/admin/document-security/summary?${queryParams}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`Failed to load document security summary: ${res.status}`);
      return res.json();
    },
  });

  const hasFilters = userId.trim() || sinceDate || untilDate;

  function clearFilters() {
    setUserId("");
    setSinceDate("");
    setUntilDate("");
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="panel-document-security">
      <div>
        <h2 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-6 h-6" />
          Document Security
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Uploads are checked for known threats before being saved. Documents that pass are stored
          safely; anything suspicious is blocked automatically.
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : error ? (
        <Alert variant="destructive" data-testid="alert-doc-security-error">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {(error as Error).message}
          </AlertDescription>
        </Alert>
      ) : data ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatTile
            label="Documents scanned"
            value={data.stats.scanned}
            icon={<FileText className="w-5 h-5" />}
            variant="neutral"
          />
          <StatTile
            label="Passed clean"
            value={data.stats.passed}
            icon={<CheckCircle2 className="w-5 h-5" />}
            variant="green"
          />
          <StatTile
            label="Blocked / flagged"
            value={data.stats.blocked}
            icon={<XCircle className="w-5 h-5" />}
            variant="red"
          />
        </div>
      ) : null}

      <Card data-testid="card-recent-uploads">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">Recent uploads</CardTitle>
              <CardDescription>
                The most recent file uploads and their scan results.
              </CardDescription>
            </div>
            {isFetching && (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mt-1" />
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs flex items-center gap-1">
                <User className="w-3 h-3" /> User ID
              </Label>
              <Input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="filter by user"
                className="h-8 text-sm w-48"
                data-testid="input-doc-security-userid"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={sinceDate}
                onChange={(e) => setSinceDate(e.target.value)}
                className="h-8 text-sm w-40"
                data-testid="input-doc-security-since"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={untilDate}
                onChange={(e) => setUntilDate(e.target.value)}
                className="h-8 text-sm w-40"
                data-testid="input-doc-security-until"
              />
            </div>
            {hasFilters && (
              <Button
                size="sm"
                variant="ghost"
                onClick={clearFilters}
                className="h-8 text-xs"
                data-testid="button-doc-security-clear-filters"
              >
                <Filter className="w-3 h-3 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : !data ? null : data.recentUploads.length === 0 ? (
            <p
              className="text-sm text-muted-foreground py-6 text-center"
              data-testid="text-no-uploads"
            >
              No document uploads found{hasFilters ? " matching these filters" : ""}.
            </p>
          ) : (
            <div className="overflow-x-auto -mx-2">
              <table
                className="w-full text-sm min-w-[500px]"
                data-testid="table-recent-uploads"
              >
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="pb-2 pr-4 font-medium pl-2">When</th>
                    <th className="pb-2 pr-4 font-medium">Who uploaded</th>
                    <th className="pb-2 pr-4 font-medium">File name</th>
                    <th className="pb-2 font-medium">Result</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentUploads.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                      data-testid={`row-upload-${row.id}`}
                    >
                      <td className="py-2.5 pr-4 text-muted-foreground text-xs whitespace-nowrap pl-2">
                        {format(new Date(row.createdAt), "MMM d, yyyy h:mm a")}
                      </td>
                      <td className="py-2.5 pr-4 text-xs font-mono truncate max-w-[140px]">
                        {row.userId ? (
                          <span title={row.userId}>{row.userId.slice(0, 20)}{row.userId.length > 20 ? "…" : ""}</span>
                        ) : (
                          <span className="text-muted-foreground italic">System</span>
                        )}
                      </td>
                      <td className="py-2.5 pr-4 truncate max-w-[200px]">
                        {row.fileName ? (
                          <span title={row.fileName}>{row.fileName}</span>
                        ) : (
                          <span className="text-muted-foreground italic">—</span>
                        )}
                      </td>
                      <td className="py-2.5">
                        <ResultBadge row={row} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
